/**
 * Custom Product V2 - Image Gallery
 * Swiper-based gallery with thumbnail navigation and position tracking
 */

class CustomProductGallery {
  constructor(container) {
    this.container = container;
    this.mainGallery = null;
    this.thumbsGallery = null;
    this.progressFill = null;
    this.breakpoint = 750;
    this.imageSwitchers = new Map(); // Store initialized switchers per slide
    this.crossfadeSwitchers = new Map(); // Store initialized crossfade switchers per slide
    this.init();
  }

  isDesktop() {
    return window.innerWidth >= this.breakpoint;
  }

  init() {
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.cacheElements();
    this.conditionalRenderSlides();
    this.initThumbs();
    this.initMainGallery();
    this.initCursorArrow();
  }
 
  cacheElements() {
    this.mainGalleryEl = this.container.querySelector('.custom-product__gallery');
    this.thumbsGalleryEl = this.container.querySelector('.custom-product__thumbs');
    this.paginationDotsEl = this.container.querySelector('.pagination-dots');
    this.cursorArrow = this.container.querySelector('.gallery-cursor-arrow');
  }

  conditionalRenderSlides() {
    if (!this.isDesktop()) return;

    const mainSlides = this.mainGalleryEl.querySelectorAll('[data-hide-on-desktop="true"]');
    const thumbSlides = this.thumbsGalleryEl?.querySelectorAll('[data-hide-on-desktop="true"]');

    mainSlides.forEach(slide => slide.remove());
    thumbSlides?.forEach(slide => slide.remove());
  }

  initThumbs() {
    if (!this.thumbsGalleryEl) return;

    if (this.thumbsGalleryEl.swiper) {
      this.thumbsGalleryEl.swiper.destroy(true, true);
    }

    this.thumbsGallery = new Swiper(this.thumbsGalleryEl, {
      spaceBetween: 10,
      slidesPerView: 'auto',
      freeMode: true,
      watchSlidesProgress: true,
    });
  }

  initMainGallery() {
    if (!this.mainGalleryEl) return;

    if (this.mainGalleryEl.swiper) {
      this.mainGalleryEl.swiper.destroy(true, true);
    }

    this.totalSlides = this.mainGalleryEl.querySelectorAll('.swiper-slide').length;
    if (this.totalSlides === 0) return;

    // Create pagination dots
    this.createPaginationDots(this.totalSlides);

    const self = this;

    this.mainGallery = new Swiper(this.mainGalleryEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      effect: 'slide',
      speed: 400,
      touchEventsTarget: 'wrapper',  // Changed from 'container' to 'wrapper'
      simulateTouch: true,
      allowTouchMove: true,
      touchRatio: 1,
      touchAngle: 45,
      threshold: 5,
      cssMode: false,
      passiveListeners: true,  // Added for better touch performance

      // Prevent swiping when starting touch on interactive elements
      noSwipingSelector: '.cp-image-block__input, .cp-image-buttons__button, button, input, textarea',

      thumbs: {
        swiper: this.thumbsGallery,
      },

      on: {
        init: function() {
          self.updatePagination(this.activeIndex);
          self.initSwitcherForSlide(this.activeIndex);
          self.initCrossfadeForSlide(this.activeIndex);
        },

        slideChange: function() {
          self.updatePagination(this.activeIndex);
          self.initSwitcherForSlide(this.activeIndex);
          self.initCrossfadeForSlide(this.activeIndex);
        },

        activeIndexChange: function() {
          self.updatePagination(this.activeIndex);
        },
      },
    });

    // Initialize swipe hint animation AFTER mainGallery is assigned
    this.initSwipeHintAnimation();
  }

  /**
   * Show a subtle swipe hint animation to indicate swipeable content
   */
  initSwipeHintAnimation() {
    // Only show hint on mobile and if there are multiple slides
    if (this.isDesktop() || !this.mainGallery || this.totalSlides <= 1) return;

    const self = this;
    const startTime = Date.now();
    const maxWaitTime = 1500; // Max 1.5 seconds

    const triggerHint = () => {
      // Double check swiper is still initialized and on first slide
      if (!self.mainGallery || self.mainGallery.activeIndex !== 0) return;

      // Get current translate position
      const currentTranslate = self.mainGallery.getTranslate();
      const slideWidth = self.mainGallery.width;
      const peekDistance = slideWidth * 0.25; // 10% peek

      // Fast 10% peek animation
      setTimeout(() => {
        // Animate to 10% position (faster: 400ms)
        self.mainGallery.wrapperEl.style.transitionDuration = '400ms';
        self.mainGallery.setTranslate(currentTranslate - peekDistance);

        // Return to original position (faster: 350ms, shorter pause: 500ms)
        setTimeout(() => {
          self.mainGallery.wrapperEl.style.transitionDuration = '350ms';
          self.mainGallery.setTranslate(currentTranslate);
        }, 500);
      }, 200);
    };

    // Check if page is already loaded
    if (document.readyState === 'complete') {
      setTimeout(triggerHint, 200);
    } else {
      // Wait for page load, but max 1.5 seconds
      const timeoutId = setTimeout(triggerHint, maxWaitTime);

      window.addEventListener('load', () => {
        clearTimeout(timeoutId);
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(200, maxWaitTime - elapsedTime);
        setTimeout(triggerHint, remainingTime);
      }, { once: true });
    }
  }

  /**
   * Initialize image button switcher for a specific slide
   */
  initSwitcherForSlide(slideIndex) {
    if (this.imageSwitchers.has(slideIndex)) {
      return;
    }

    const slides = this.mainGalleryEl.querySelectorAll('.swiper-slide');
    const currentSlide = slides[slideIndex];

    if (!currentSlide) {
      return;
    }

    const switcherElement = currentSlide.querySelector('[data-image-switcher]');

    if (switcherElement) {
      const switcher = new ImageButtonSwitcher(switcherElement);
      this.imageSwitchers.set(slideIndex, switcher);
    }
  }

  /**
   * Initialize crossfade switcher for a specific slide
   */
  initCrossfadeForSlide(slideIndex) {
    if (this.crossfadeSwitchers.has(slideIndex)) {
      return;
    }

    const slides = this.mainGalleryEl.querySelectorAll('.swiper-slide');
    const currentSlide = slides[slideIndex];

    if (!currentSlide) {
      return;
    }

    const crossfadeContainer = currentSlide.querySelector('[data-crossfade-container]');

    if (crossfadeContainer) {
      const crossfade = new CrossfadeSwitcher(crossfadeContainer, currentSlide);
      if (crossfade.isValid) {
        this.crossfadeSwitchers.set(slideIndex, crossfade);
      }
    }
  }

  createPaginationDots(totalSlides) {
    if (!this.paginationDotsEl || totalSlides <= 1) return;

    this.paginationDotsEl.innerHTML = '';

    for (let i = 0; i < totalSlides; i++) {
      const dot = document.createElement('div');
      dot.classList.add('pagination-dot');
      if (i === 0) dot.classList.add('active');

      dot.addEventListener('click', () => {
        if (this.mainGallery) {
          this.mainGallery.slideTo(i);
        }
      });

      this.paginationDotsEl.appendChild(dot);
    }
  }

  updatePagination(activeIndex) {
    if (!this.paginationDotsEl) return;

    const dots = this.paginationDotsEl.querySelectorAll('.pagination-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }

  initCursorArrow() {
    if (!this.isDesktop() || !this.cursorArrow || !this.mainGalleryEl || !this.mainGallery) return;

    const arrow = this.cursorArrow;
    const gallery = this.mainGalleryEl;
    const svg = arrow.querySelector('svg');

    // Mouse move handler
    gallery.addEventListener('mousemove', (e) => {
      const rect = gallery.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;

      // Position arrow at cursor (offset by half arrow size for centering)
      arrow.style.transform = `translate(${x - 32}px, ${y - 32}px)`;

      // Rotate based on which side of gallery
      if (x < centerX) {
        // Left side - rotate 180deg (point left)
        svg.style.transform = 'rotate(180deg)';
      } else {
        // Right side - no rotation (point right)
        svg.style.transform = 'rotate(0deg)';
      }
    });

    // Click handler
    gallery.addEventListener('click', (e) => {
      const rect = gallery.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const centerX = rect.width / 2;

      if (x < centerX) {
        // Left side - go to previous
        if (!this.mainGallery.isBeginning) {
          this.mainGallery.slidePrev();
        }
      } else {
        // Right side - go to next
        if (!this.mainGallery.isEnd) {
          this.mainGallery.slideNext();
        }
      }
    });

    // Set cursor style
    gallery.style.cursor = 'none';
  }
}

/**
 * Crossfade Switcher for Benefits Slides
 * Automatically crossfades between multiple images when slide is 60% visible
 */
class CrossfadeSwitcher {
  static CROSSFADE_INTERVAL = 2600; // 2.6 seconds between transitions
  static TRANSITION_DURATION = 500; // 500ms fade transition
  static VISIBILITY_THRESHOLD = 0.6; // 60% visibility required

  constructor(container, slideElement) {
    this.container = container;
    this.slideElement = slideElement;
    this.slideId = container.getAttribute('data-crossfade-container');
    this.images = [];
    this.currentIndex = 0;
    this.intervalId = null;
    this.isActive = false;
    this.observer = null;
    this.isValid = false;

    if (!this.validate()) {
      return;
    }

    this.isValid = true;
    this.init();
  }

  validate() {
    if (!this.slideId || !this.container) {
      return false;
    }

    // Get all crossfade images for this slide
    this.images = Array.from(
      this.container.querySelectorAll(`.crossFade__image[data-slide-id="${this.slideId}"]`)
    );

    // Need at least 2 images for crossfade
    if (this.images.length <= 1) {
      return false;
    }

    return true;
  }

  init() {
    this.preloadImages(() => {
      this.setupIntersectionObserver();
      this.checkInitialVisibility();
    });
  }

  checkInitialVisibility() {
    const rect = this.slideElement.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    // Calculate visible height
    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, windowHeight);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    
    // Calculate intersection ratio (same logic as Intersection Observer)
    const intersectionRatio = rect.height > 0 ? visibleHeight / rect.height : 0;
    
    // Start if 60% or more is visible (matching VISIBILITY_THRESHOLD)
    if (intersectionRatio >= CrossfadeSwitcher.VISIBILITY_THRESHOLD) {
      this.startCrossfade();
    }
  }

  preloadImages(callback) {
    const imageSources = this.images
      .map(img => img.getAttribute('src'))
      .filter(Boolean);

    if (imageSources.length === 0) {
      callback();
      return;
    }

    let loadedCount = 0;
    const totalCount = imageSources.length;

    imageSources.forEach(src => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalCount) {
          callback();
        }
      };
      img.src = src;
    });
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= CrossfadeSwitcher.VISIBILITY_THRESHOLD) {
          this.startCrossfade();
        } else {
          this.stopCrossfade();
          // No reset - keep current image when slide becomes less visible
        }
      });
    }, {
      threshold: CrossfadeSwitcher.VISIBILITY_THRESHOLD,
      rootMargin: '0px'
    });

    this.observer.observe(this.slideElement);
  }

  startCrossfade() {
    if (this.isActive) return;

    this.isActive = true;
    
    // Show first image immediately
    this.images[this.currentIndex].classList.add('crossFade__image--active');

    // Start cycling through images
    this.intervalId = setInterval(() => {
      this.showNextImage();
    }, CrossfadeSwitcher.CROSSFADE_INTERVAL);
  }

  stopCrossfade() {
    if (!this.isActive) return;

    this.isActive = false;
    
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  showNextImage() {
    const nextIndex = (this.currentIndex + 1) % this.images.length;
    const currentImage = this.images[this.currentIndex];
    const nextImage = this.images[nextIndex];

    // Fade in next image
    nextImage.classList.add('crossFade__image--active');

    // Fade out current image after transition starts
    setTimeout(() => {
      currentImage.classList.remove('crossFade__image--active');
    }, CrossfadeSwitcher.TRANSITION_DURATION);

    this.currentIndex = nextIndex;
  }

  destroy() {
    this.stopCrossfade();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

/**
 * Image Button Switcher for Image Buttons Block
 * With auto-cycling and Intersection Observer
 */
class ImageButtonSwitcher {
  static CYCLE_INTERVAL = 2000;
  static VISIBILITY_THRESHOLD = 0.5;

  constructor(switcher) {
    this.switcher = switcher;
    this.imageContainer = switcher.querySelector('[data-image-container]');
    this.buttons = Array.from(switcher.querySelectorAll('.cp-image-buttons__button[data-option]'));
    this.currentIndex = 0;
    this.intervalId = null;
    this.isAutoCycling = false;
    this.userInteracted = false;
    this.observer = null;
    this.isValid = false;
    this.boundClickHandlers = [];

    if (!this.validate()) {
      return;
    }

    this.isValid = true;
    this.init();
  }

  validate() {
    if (!this.imageContainer || this.buttons.length === 0) {
      return false;
    }
    return true;
  }

  init() {
    const selectedButton = this.buttons.find(btn =>
      btn.classList.contains('cp-image-buttons__button--selected')
    );

    if (selectedButton) {
      this.currentIndex = this.buttons.indexOf(selectedButton);
    }

    this.attachEventListeners();
    this.setupIntersectionObserver();
  }

  attachEventListeners() {
    this.buttons.forEach((button, index) => {
      const handler = () => this.handleButtonClick(index);
      this.boundClickHandlers.push({ button, handler });
      button.addEventListener('click', handler);
    });
  }

  handleButtonClick(index) {
    this.userInteracted = true;
    this.stopAutoCycle();
    this.switchToButton(index);
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.userInteracted) {
          this.startAutoCycle();
        } else {
          this.stopAutoCycle();
        }
      });
    }, {
      threshold: ImageButtonSwitcher.VISIBILITY_THRESHOLD,
      rootMargin: '0px'
    });

    this.observer.observe(this.switcher);
  }

  startAutoCycle() {
    if (this.isAutoCycling || this.userInteracted) return;

    this.isAutoCycling = true;
    this.intervalId = setInterval(() => {
      this.cycleNext();
    }, ImageButtonSwitcher.CYCLE_INTERVAL);
  }

  stopAutoCycle() {
    if (!this.isAutoCycling) return;

    this.isAutoCycling = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  cycleNext() {
    const nextIndex = (this.currentIndex + 1) % this.buttons.length;
    this.switchToButton(nextIndex);
  }

  switchToButton(index) {
    if (index === this.currentIndex) return;

    const button = this.buttons[index];
    if (!button) return;

    const option = button.dataset.option;
    if (!option) return;

    const imageUrl = this.imageContainer.dataset[option];
    if (!imageUrl) return;

    const previousButton = this.buttons[this.currentIndex];
    if (previousButton) {
      previousButton.classList.remove('cp-image-buttons__button--selected');
    }

    button.classList.add('cp-image-buttons__button--selected');
    this.imageContainer.style.backgroundImage = `url('${imageUrl}')`;
    this.currentIndex = index;
  }

  destroy() {
    this.stopAutoCycle();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.boundClickHandlers.forEach(({ button, handler }) => {
      button.removeEventListener('click', handler);
    });
    this.boundClickHandlers = [];
  }
}

/**
 * Variant Selector - Handles Material and Size Dropdowns
 */
class VariantSelector {
  constructor(container) {
    this.container = container;
    this.config = null;
    this.selectedMaterial = 'poster'; // Default
    this.selectedSize = '30x40'; // Default
    this.init();
  }

  async init() {
    try {
      // Dynamically import config
      const { PRICE_MAPPING, MATERIAL_GROUPS, SIZE_LABELS } = await import('./product-variants-config.js');
      this.priceMapping = PRICE_MAPPING;
      this.materialGroups = MATERIAL_GROUPS;
      this.sizeLabels = SIZE_LABELS;

      this.cacheElements();
      this.populateMaterialDropdown();
      this.populateSizeDropdown();
      this.attachEventListeners();
      this.updatePrice();
    } catch (error) {
      console.error('[VariantSelector] Failed to load config:', error);
    }
  }

  cacheElements() {
    this.materialSelect = this.container.querySelector('[data-variant-material]');
    this.sizeSelect = this.container.querySelector('[data-variant-size]');
    this.priceDisplay = this.container.querySelector('[data-variant-price]');
  }

  populateMaterialDropdown() {
    if (!this.materialSelect || !this.materialGroups) return;

    // Clear existing options
    this.materialSelect.innerHTML = '';

    // Add options for each material group
    Object.entries(this.materialGroups).forEach(([groupKey, groupData]) => {
      const option = document.createElement('option');
      option.value = groupData.materialKey; // Store actual material key
      option.textContent = groupData.label; // Display simplified label

      // Select default
      if (groupData.materialKey === this.selectedMaterial) {
        option.selected = true;
      }

      this.materialSelect.appendChild(option);
    });
  }

  populateSizeDropdown() {
    if (!this.sizeSelect || !this.priceMapping) return;

    // Clear existing options
    this.sizeSelect.innerHTML = '';

    // Get available sizes for selected material
    const availableSizes = Object.keys(this.priceMapping[this.selectedMaterial] || {});

    // Add options for each size
    availableSizes.forEach(sizeKey => {
      const option = document.createElement('option');
      option.value = sizeKey;
      option.textContent = this.sizeLabels[sizeKey] || sizeKey;

      // Select default
      if (sizeKey === this.selectedSize) {
        option.selected = true;
      }

      this.sizeSelect.appendChild(option);
    });

    // If current size is not available for new material, select first available
    if (!availableSizes.includes(this.selectedSize) && availableSizes.length > 0) {
      this.selectedSize = availableSizes[0];
      this.sizeSelect.value = this.selectedSize;
    }
  }

  attachEventListeners() {
    if (this.materialSelect) {
      this.materialSelect.addEventListener('change', (e) => {
        this.selectedMaterial = e.target.value;
        this.populateSizeDropdown(); // Update size options
        this.updatePrice();
      });
    }

    if (this.sizeSelect) {
      this.sizeSelect.addEventListener('change', (e) => {
        this.selectedSize = e.target.value;
        this.updatePrice();
      });
    }
  }

  updatePrice() {
    if (!this.priceDisplay || !this.priceMapping) return;

    // Get all sizes for the selected material
    const materialSizes = this.priceMapping[this.selectedMaterial];

    if (!materialSizes) {
      this.priceDisplay.textContent = '—';
      return;
    }

    // Find the lowest price for this material
    const prices = Object.values(materialSizes).map(data => data.price);
    const lowestPrice = Math.min(...prices);

    if (lowestPrice) {
      // Format price in German format without decimals for whole numbers (e.g., "29€" or "29,50€")
      const formattedPrice = lowestPrice % 1 === 0
        ? `${lowestPrice.toFixed(0)}€`
        : `${lowestPrice.toFixed(2).replace('.', ',')}€`;

      // Always show "ab" prefix since we're showing the starting price
      this.priceDisplay.textContent = `ab ${formattedPrice}`;
    } else {
      this.priceDisplay.textContent = '—';
    }
  }

  getCurrentVariant() {
    return {
      material: this.selectedMaterial,
      size: this.selectedSize,
      price: this.priceMapping[this.selectedMaterial]?.[this.selectedSize]?.price || 0,
      ratio: this.priceMapping[this.selectedMaterial]?.[this.selectedSize]?.ratio || 0
    };
  }
}

/**
 * Initialize all custom product galleries and variant selectors on the page
 */
document.addEventListener('DOMContentLoaded', () => {
  const galleries = document.querySelectorAll('.custom-product');
  galleries.forEach(gallery => {
    new CustomProductGallery(gallery);
    new VariantSelector(gallery);
  });
});
